import React, { useState, useEffect, useRef, useCallback } from 'react';

// RUTAS DE TUS IMÁGENES (Ajusta según tu proyecto)
const ASSETS = {
    BASKET: '/img/games/picnic/canasta.png',
    FRUIT_1: '/img/games/picnic/manzana.png',
    FRUIT_2: '/img/games/picnic/platano.png',
    FLY_ALIVE: '/img/games/picnic/mosca.png',
    FLY_SMASHED: '/img/games/picnic/mosca-aplastada.png',
    SWATTER: '/img/games/picnic/matamoscas.png'
};

const Picnic = ({ onGameFinishEvent, onCurrentScoreChange, gameConfiguration }) => {
    // ========================================================
    // ESTADOS DEL JUEGO
    // ========================================================
    const [currentScore, setCurrentScore] = useState(0);
    const [secondsRemaining, setSecondsRemaining] = useState(gameConfiguration.roundDurationSeconds);
    const [isGameRunning, setIsGameRunning] = useState(true);
    
    // Arrays que guardan los elementos renderizados en pantalla
    const [fallingObjects, setFallingObjects] = useState([]);
    const [floatingFeedbacks, setFloatingFeedbacks] = useState([]);
    
    // Efectos visuales de pantalla
    const [isRedFlashActive, setIsRedFlashActive] = useState(false);
    const [swatterAnimationData, setSwatterAnimationData] = useState({ isVisible: false, x: 0, y: 0, rotation: 0 });

    // Referencias para el Loop de Animación (Evitan cierres de variables obsoletas)
    const animationFrameId = useRef(null);
    const lastTickTime = useRef(Date.now());
    const timeSinceLastFruit = useRef(0);
    const timeSinceLastFly = useRef(0);

    // ========================================================
    // MOTOR DE FÍSICAS (Game Loop 60FPS)
    // ========================================================
    const gameTick = useCallback(() => {
        if (!isGameRunning) return;

        const currentTime = Date.now();
        const deltaTime = currentTime - lastTickTime.current;
        lastTickTime.current = currentTime;

        // 1. DINAMISMO DE DIFICULTAD (Aumenta conforme baja el tiempo)
        const difficultyMultiplier = 1 + ((gameConfiguration.roundDurationSeconds - secondsRemaining) * 0.02);
        
        // 2. GENERADOR DE FRUTAS (Spawners)
        timeSinceLastFruit.current += deltaTime;
        if (timeSinceLastFruit.current > (gameConfiguration.fruitSpawnRateMs / difficultyMultiplier)) {
            timeSinceLastFruit.current = 0;
            spawnNewObject('fruit');
        }

        // 3. GENERADOR DE MOSCAS (Spawners)
        timeSinceLastFly.current += deltaTime;
        if (timeSinceLastFly.current > (gameConfiguration.flySpawnRateMs / difficultyMultiplier)) {
            timeSinceLastFly.current = 0;
            spawnNewObject('fly');
        }

        // 4. ACTUALIZAR POSICIONES Y DETECTAR COLISIONES
        // setFallingObjects(previousObjects => {
        //     const updatedObjects = [];

        //     for (let i = 0; i < previousObjects.length; i++) {
        //         const currentObject = previousObjects[i];

        //         // Si la mosca está aplastada, se queda quieta en la pantalla un ratito
        //         if (currentObject.isSmashed) {
        //             currentObject.smashedTimer -= deltaTime;
        //             if (currentObject.smashedTimer > 0) updatedObjects.push(currentObject);
        //             continue; // Pasa al siguiente objeto
        //         }

        //         // Cálculo de movimiento
        //         const currentSpeed = currentObject.speed * difficultyMultiplier;
        //         currentObject.y += currentSpeed;

        //         // Movimiento Zig-Zag exclusivo para moscas vivas
        //         if (currentObject.type === 'fly') {
        //             currentObject.zigZagPhase += 0.1;
        //             // Oscila entre -3 y +3 puntos porcentuales horizontales
        //             currentObject.currentX = currentObject.baseX + Math.sin(currentObject.zigZagPhase) * 5; 
        //         } else {
        //             currentObject.currentX = currentObject.baseX; // Las frutas caen recto
        //         }

        //         // Detectar si el objeto llegó a la canasta (Fondo de la pantalla > 90%)
        //         if (currentObject.y >= 90) {
        //             if (!currentObject.hasBeenCounted) {
        //                 currentObject.hasBeenCounted = true;

        //                 if (currentObject.type === 'fruit') {
        //                     // ¡Fruta en la canasta! Suma puntos
        //                     updateScore(gameConfiguration.fruitScore);
        //                     spawnFloatingText(`+${gameConfiguration.fruitScore}`, currentObject.currentX, 85, '#10b981'); // Verde
        //                 } 
        //                 else if (currentObject.type === 'fly') {
        //                     // ¡Mosca en la canasta! Resta puntos
        //                     updateScore(-gameConfiguration.flyPenalty);
        //                     spawnFloatingText(`-${gameConfiguration.flyPenalty}`, currentObject.currentX, 85, '#ef4444'); // Rojo
        //                     triggerRedFlashPenalty();
        //                 }
        //             }
        //             // No lo metemos al array de actualizados, así desaparece (simulando que cayó dentro)
        //         } else {
        //             // Si aún no cae, lo mantenemos en el array para el próximo frame
        //             updatedObjects.push(currentObject);
        //         }
        //     }

        //     return updatedObjects;
        // });

        setFallingObjects(previousObjects => {
            const updatedObjects = [];
            let localScoreDelta = 0;
            let textsToSpawn = [];
            let flash = false;

            for (let i = 0; i < previousObjects.length; i++) {
                const currentObject = previousObjects[i];

                if (currentObject.isSmashed) {
                    currentObject.smashedTimer -= deltaTime;
                    if (currentObject.smashedTimer > 0) updatedObjects.push(currentObject);
                    continue;
                }

                // IMPORTANTE: Clonar el objeto para no mutar el estado original directamente
                const newObj = { ...currentObject };

                const currentSpeed = newObj.speed * difficultyMultiplier;
                newObj.y += currentSpeed;

                if (newObj.type === 'fly') {
                    newObj.zigZagPhase += 0.1;
                    newObj.currentX = newObj.baseX + Math.sin(newObj.zigZagPhase) * 5;
                } else {
                    newObj.currentX = newObj.baseX;
                }

                if (newObj.y >= 90) {
                    if (!newObj.hasBeenCounted) {
                        newObj.hasBeenCounted = true;
                        if (newObj.type === 'fruit') {
                            localScoreDelta += (gameConfiguration.fruitScore || 1);
                            textsToSpawn.push({ text: `+${gameConfiguration.fruitScore || 1}`, x: newObj.currentX, y: 85, color: '#10b981' });
                        } else if (newObj.type === 'fly') {
                            localScoreDelta -= (gameConfiguration.flyPenalty || 1);
                            textsToSpawn.push({ text: `-${gameConfiguration.flyPenalty || 1}`, x: newObj.currentX, y: 85, color: '#ef4444' });
                            flash = true;
                        }
                    }
                } else {
                    updatedObjects.push(newObj);
                }
            }

            // EXTRAER LOS EFECTOS SECUNDARIOS:
            // Al usar setTimeout a 0ms, permitimos que React termine de procesar 
            // setFallingObjects antes de disparar las otras actualizaciones de estado.
            if (localScoreDelta !== 0 || textsToSpawn.length > 0 || flash) {
                setTimeout(() => {
                    if (localScoreDelta !== 0) updateScore(localScoreDelta);
                    if (flash) triggerRedFlashPenalty();
                    textsToSpawn.forEach(t => spawnFloatingText(t.text, t.x, t.y, t.color));
                }, 0);
            }

            return updatedObjects;
        });

        // Llamar al próximo frame
        animationFrameId.current = requestAnimationFrame(gameTick);

    }, [isGameRunning, secondsRemaining, gameConfiguration]);

    // Arrancar y limpiar el Game Loop
    useEffect(() => {
        lastTickTime.current = Date.now();
        animationFrameId.current = requestAnimationFrame(gameTick);
        return () => cancelAnimationFrame(animationFrameId.current);
    }, [gameTick]);

    // // ========================================================
    // // RELOJ DEL JUEGO (Timer de ronda)
    // // ========================================================
    // useEffect(() => {
    //     if (!isGameRunning) return;

    //     const timerInterval = setInterval(() => {
    //         setSecondsRemaining(prev => {
    //             if (prev <= 1) {
    //                 clearInterval(timerInterval);
    //                 setIsGameRunning(false); // Detiene las físicas
    //                 onGameFinishEvent(currentScore); // Avisa al padre que terminó
    //                 return 0;
    //             }
    //             return prev - 1;
    //         });
    //     }, 1000);

    //     return () => clearInterval(timerInterval);
    // }, [isGameRunning, currentScore, onGameFinishEvent]);

    // ========================================================
    // RELOJ DEL JUEGO (Timer de ronda)
    // ========================================================
    
    // 1. Efecto Puro: Solamente descuenta el tiempo cada 1000ms.
    // Al quitar "currentScore" de las dependencias, evitamos que los puntos reinicien el reloj.
    useEffect(() => {
        if (!isGameRunning) return;

        const timerInterval = setInterval(() => {
            setSecondsRemaining(prev => prev - 1);
        }, 1000);

        return () => clearInterval(timerInterval);
    }, [isGameRunning]);

    // 2. Efecto Vigilante: Detecta cuándo el reloj llega a cero para detener el juego localmente.
    useEffect(() => {
        if (isGameRunning && secondsRemaining <= 0) {
            setIsGameRunning(false); // Detiene las físicas y la caída de objetos
            onGameFinishEvent(currentScore); // Avisa al componente padre (PicnicFan.js)
        }
    }, [secondsRemaining, isGameRunning, currentScore, onGameFinishEvent]);

    // ========================================================
    // HELPERS DE LÓGICA Y ANIMACIÓN
    // ========================================================
    // const updateScore = (amount) => {
    //     setCurrentScore(prevScore => prevScore + amount);
    // };

    // const updateScore = (amount) => {
    //     setCurrentScore(prevScore => {
    //         const newlyCalculatedScore = prevScore + amount;
            
    //         // NUEVO: Le avisamos al componente padre que la puntuación cambió en vivo
    //         if (onCurrentScoreChange) {
    //             onCurrentScoreChange(newlyCalculatedScore);
    //         }
            
    //         return newlyCalculatedScore;
    //     });
    // };

    const updateScore = (amount) => {
        setCurrentScore(prevScore => prevScore + amount);
    };

    // Añade este useEffect justo debajo:
    useEffect(() => {
        if (onCurrentScoreChange) {
            onCurrentScoreChange(currentScore);
        }
    }, [currentScore, onCurrentScoreChange]);

    const spawnNewObject = (typeToSpawn) => {
        const newObject = {
            id: `obj_${Date.now()}_${Math.random()}`,
            type: typeToSpawn,
            imageAsset: typeToSpawn === 'fruit' ? (Math.random() > 0.5 ? ASSETS.FRUIT_1 : ASSETS.FRUIT_2) : ASSETS.FLY_ALIVE,
            baseX: Math.floor(Math.random() * 80) + 10, // Posición horizontal aleatoria (10% a 90%)
            currentX: 0,
            y: -10, // Nace arriba fuera de la pantalla
            speed: gameConfiguration.baseFallSpeed + (Math.random() * 0.3), // Velocidad base + variación
            zigZagPhase: Math.random() * 10,
            isSmashed: false,
            hasBeenCounted: false,
            smashedTimer: 1000 // Milisegundos que se quedará pegada como mancha
        };
        setFallingObjects(prev => [...prev, newObject]);
    };

    const spawnFloatingText = (textContent, xPos, yPos, textColor) => {
        const newText = { id: Date.now(), text: textContent, x: xPos, y: yPos, color: textColor };
        setFloatingFeedbacks(prev => [...prev, newText]);
        // Autoeliminar texto después de la animación (1 segundo)
        setTimeout(() => {
            setFloatingFeedbacks(prev => prev.filter(item => item.id !== newText.id));
        }, 1000);
    };

    const triggerRedFlashPenalty = () => {
        setIsRedFlashActive(true);
        // Quitar el flash rápido
        setTimeout(() => setIsRedFlashActive(false), 200);
    };

    // ========================================================
    // INTERACCIÓN DEL JUGADOR (El "Matamoscas")
    // ========================================================
    const handlePointerDownOnObject = (event, clickedObject) => {
        // Ignorar si el juego paró o si ya aplastaron la mosca
        if (!isGameRunning || clickedObject.isSmashed) return;

        if (clickedObject.type === 'fly') {
            // 1. Marcar como aplastada
            setFallingObjects(prev => prev.map(obj => 
                obj.id === clickedObject.id 
                    ? { ...obj, isSmashed: true, imageAsset: ASSETS.FLY_SMASHED } 
                    : obj
            ));

            // 2. Sumar puntos y mostrar Onomatopeya
            // Le ponemos un fallback de 2 puntos por si la variable viene vacía
            const rewardPoints = gameConfiguration.flySmashedReward || 2; 
            
            updateScore(rewardPoints);
            spawnFloatingText(`+${rewardPoints}`, clickedObject.currentX, clickedObject.y, '#f59e0b'); // Naranja

            // // 2. Sumar puntos y mostrar Onomatopeya
            // updateScore(gameConfiguration.flySmashedReward);
            // spawnFloatingText(`+${gameConfiguration.flySmashedReward}`, clickedObject.currentX, clickedObject.y, '#f59e0b'); // Naranja

            // 3. Mostrar Animación del Matamoscas
            // Rotación aleatoria para que cada golpe se sienta natural
            const randomRotation = (Math.random() * 60) - 30; // Entre -30 y 30 grados
            setSwatterAnimationData({ isVisible: true, x: clickedObject.currentX, y: clickedObject.y, rotation: randomRotation });
            
            setTimeout(() => {
                setSwatterAnimationData(prev => ({ ...prev, isVisible: false }));
            }, 300); // El matamoscas desaparece en 300ms
        }
        else if (clickedObject.type === 'fruit') {
            // FUTURA EXPANSIÓN: Penalización por aplastar fruta
            /*
            setFallingObjects(prev => prev.filter(obj => obj.id !== clickedObject.id));
            updateScore(-1);
            spawnFloatingText("-1", clickedObject.currentX, clickedObject.y, '#6b7280'); // Gris
            */
        }
    };


    // ========================================================
    // RENDERIZADO DEL CAMPO DE JUEGO
    // ========================================================
    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', userSelect: 'none', touchAction: 'none' }}>
            
            {/* INYECCIÓN DE ANIMACIONES CSS CLAVE */}
            <style>
                {`
                    @keyframes floatUpFade {
                        0% { transform: translateY(0px) scale(1); opacity: 1; }
                        100% { transform: translateY(-50px) scale(1.5); opacity: 0; }
                    }
                    .floating-onomatopoeia {
                        position: absolute;
                        font-family: 'Impact', sans-serif;
                        font-size: 2.5rem;
                        font-weight: 900;
                        -webkit-text-stroke: 2px white;
                        pointer-events: none;
                        z-index: 50;
                        animation: floatUpFade 1s forwards ease-out;
                        transform: translate(-50%, -50%);
                    }
                    .swatter-smash {
                        position: absolute;
                        width: 120px;
                        height: auto;
                        transform-origin: bottom right;
                        pointer-events: none;
                        z-index: 40;
                        transition: transform 0.05s ease-in;
                    }
                `}
            </style>

            {/* PANTALLAZO ROJO (Penalización) */}
            <div style={{
                position: 'absolute', inset: 0, backgroundColor: 'red', zIndex: 100, pointerEvents: 'none',
                opacity: isRedFlashActive ? 0.4 : 0, transition: 'opacity 0.1s linear'
            }}></div>

            {/* MARCADOR SUPERIOR (HUD) */}
            <div style={{ position: 'absolute', top: '20px', left: '20px', right: '20px', display: 'flex', justifyContent: 'space-between', zIndex: 20 }}>
                <div style={{ color: '#000', backgroundColor: 'rgba(255,255,255,0.9)', padding: '10px 20px', borderRadius: '15px', fontWeight: '900', fontSize: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    PUNTOS: <span style={{ color: '#ea580c' }}>{currentScore}</span>
                </div>
                <div style={{ backgroundColor: 'rgba(0,0,0,0.8)', color: 'white', padding: '10px 20px', borderRadius: '15px', fontWeight: '900', fontSize: '1.5rem' }}>
                    ⏱ {secondsRemaining}s
                </div>
            </div>

            {/* OBJETOS CAYENDO (Frutas y Moscas) */}
            {fallingObjects.map(obj => (
                <div 
                    key={obj.id}
                    // onPointerDown es vital: reacciona en el milisegundo que el dedo toca la pantalla, sin delay.
                    onPointerDown={(e) => handlePointerDownOnObject(e, obj)}
                    style={{
                        position: 'absolute',
                        left: `${obj.currentX}%`,
                        top: `${obj.y}%`,
                        transform: 'translate(-50%, -50%)',
                        // HITBOX: Le damos padding invisible para que sea más fácil atinarle con un dedo grueso
                        padding: obj.type === 'fly' ? '25px' : '10px',
                        cursor: 'crosshair',
                        zIndex: obj.type === 'fly' ? 10 : 5 // Las moscas pasan por encima de la fruta
                    }}
                >
                    <img 
                        src={obj.imageAsset} 
                        alt={obj.type}
                        style={{ 
                            width: obj.type === 'fly' ? '60px' : '50px', 
                            height: 'auto',
                            // Efecto estético: Las moscas aplastadas se encogen un poquito
                            transform: obj.isSmashed ? 'scale(0.8)' : 'scale(1)',
                            transition: 'transform 0.1s'
                        }} 
                        draggable="false" // Previene que el navegador intente "arrastrar" la imagen en móvil
                    />
                </div>
            ))}

            {/* ANIMACIÓN DEL MATAMOSCAS */}
            {swatterAnimationData.isVisible && (
                <img 
                    src={ASSETS.SWATTER} 
                    alt="Golpe"
                    className="swatter-smash"
                    style={{
                        left: `${swatterAnimationData.x}%`,
                        top: `${swatterAnimationData.y}%`,
                        transform: `translate(-20%, -80%) rotate(${swatterAnimationData.rotation}deg)`
                    }}
                />
            )}

            {/* ONOMATOPEYAS DE TEXTO FLOTANTE (+2, -3, etc) */}
            {floatingFeedbacks.map(fb => (
                <div 
                    key={fb.id} 
                    className="floating-onomatopoeia"
                    style={{
                        left: `${fb.x}%`,
                        top: `${fb.y}%`,
                        color: fb.color
                    }}
                >
                    {fb.text}
                </div>
            ))}

            {/* CANASTA INFERIOR */}
            <div style={{ position: 'absolute', bottom: '-10px', left: '50%', transform: 'translateX(-50%)', zIndex: 15, width: '100%', textAlign: 'center', pointerEvents: 'none' }}>
                <img 
                    src={ASSETS.BASKET} 
                    alt="Canasta" 
                    style={{ width: '80%', maxWidth: '350px', filter: 'drop-shadow(0 -5px 15px rgba(0,0,0,0.3))' }} 
                />
            </div>

        </div>
    );
};

export { Picnic };